// @flow

import _ from 'lodash';
import { $set, update } from 'qim';

import { decodeHTMLEntities } from '@zapier/common-utils';

import _str from 'app/common/underscore-string';
import fuzzysearch from 'app/common/fuzzysearch';
import z from 'app/common/z';

import type { Keys, StringMap } from 'app/common/types';
import type { ServiceAction } from 'app/service/types';

const PRETTY_TYPE_BY_TYPE = {
  read: 'Trigger',
  write: 'Action',
  search: 'Action',
  search_or_write: 'Action',
  filter: 'Filter',
};

// Use this as a default object for applicable cases
// like for a key in `WeakMap` (for memoization) or
// as a default value that would keep a component pure,
// and so on.
export const emptyObject = Object.freeze({});

const vowels = {
  a: true,
  e: true,
  i: true,
  o: true,
  u: true,
};

const MISSING = undefined;

const getKeys = (keys: ?Keys): string[] => {
  if (keys == null) {
    return [];
  }
  if (!Array.isArray(keys)) {
    return [keys];
  }
  return keys;
};

export const get = (
  thing: Function | ?{} | ?Array<any>,
  keys: Keys,
  defaultValue: mixed
): any => {
  let context = null;
  let keyIndex = 0;

  const keysArray = getKeys(keys);

  while (keyIndex < keysArray.length && thing != null) {
    const key: any = keysArray[keyIndex];
    if (_.isArray(key)) {
      if (typeof thing === 'function') {
        thing = thing.apply(context, key);
        context = null;
      } else {
        thing = undefined;
      }
    } else if (thing != null) {
      context = thing;
      // key can be number or string, cast to silence flow
      thing = thing[key];
    }
    keyIndex++;
  }

  if (thing == null && defaultValue != null) {
    return defaultValue;
  }

  return thing;
};

export const exists = (thing: any, ...rest: Keys[]) => {
  if (rest.length > 0) {
    thing = get(thing, ...rest);
    return exists(thing);
  }

  return typeof thing !== 'undefined' && thing !== null;
};

export const set = (thing: Function | {}, keys: string[], value: any) => {
  // once we turn on flow everywhere we can remove these dynamic type check:
  if (
    thing === null ||
    (typeof thing !== 'object' && typeof thing !== 'function')
  ) {
    throw new Error('Must use set on an object or function.');
  }
  if (keys.length === 0) {
    throw new Error('Must provide keys to set an object.');
  }
  if (typeof keys === 'string') {
    throw new Error('keys must be an array, not a string');
  }

  for (let keyIndex = 0; keyIndex < keys.length - 1; keyIndex++) {
    const key = keys[keyIndex];
    const subThing = thing[key];
    if (
      !exists(subThing) ||
      (typeof subThing !== 'object' && typeof subThing !== 'function')
    ) {
      thing[key] = {};
    }
    thing = thing[key];
  }

  thing[keys[keys.length - 1]] = value;
};

export const deepSearch = <T>(
  query: string,
  value: T,
  depth: number = 0
): ?T => {
  // console.log(depth, value);
  if (Array.isArray(value)) {
    let outValue = value
      .map(v => deepSearch(query, v, depth + 1))
      .filter(v => v !== MISSING);
    if (_.isEmpty(outValue) && depth) {
      outValue = MISSING;
    }
    // console.log('out array:', value, outValue);
    return outValue;
  } else if (_.isObject(value)) {
    let outValue = _.chain(value)
      .toPairs()
      .map(([k, v]) => {
        const kSearch = deepSearch(query, k, depth + 1);
        const vSearch = deepSearch(query, v, depth + 1);
        if (kSearch === MISSING && vSearch === MISSING) {
          return MISSING;
        }
        return [kSearch || k, vSearch || v];
      })
      .filter(v => v !== MISSING)
      .fromPairs()
      .value();
    if (_.isEmpty(outValue) && depth) {
      outValue = MISSING;
    }
    // console.log('out obj:', value, outValue);
    return outValue;
  } else {
    let outValue = value;
    if (!fuzzysearch(query, String(value))) {
      outValue = MISSING;
    }
    // console.log('out single:', value, outValue);
    return outValue;
  }
};

export const objectToMap = (obj: {}): Map<*, *> => {
  // IE11 only supports no-arg Map constructor.
  const map = new Map();
  // Don't use _.forEach because it will iterate over a collection
  // that is either an array or object, and we don't want it to
  // iterate over an object.
  _.keys(obj).forEach(key => {
    map.set(key, obj[key]);
  });
  return map;
};

type SearchMap = Map<string, *>;

export const deepSearchMap = (query: string, map: SearchMap) => {
  if (!query) {
    return map;
  }

  const result: SearchMap = new Map();

  // IE11 does not support `for (const [key, value] in map)` style iterables
  map.forEach((value, key) => {
    const hasKeyMatch = fuzzysearch(query, key);
    if (_.isObject(value)) {
      const valueResult = deepSearchMap(query, objectToMap(value));
      if (hasKeyMatch || valueResult.size) {
        result.set(key, value);
      }
    } else {
      const hasValueMatch = fuzzysearch(query, String(value));
      if (hasKeyMatch || hasValueMatch) {
        result.set(key, value);
      }
    }
  });

  return result;
};

export const createPrefixedConstants = (prefix: string, constants: string) => {
  return _.fromPairs(
    constants
      .split(/[ \n]+/)
      .filter(part => part)
      .map(part => [part, prefix ? `${prefix}/${part}` : part])
  );
};

export const createConstants = _.partial(createPrefixedConstants, '');

export const visibleActions = (
  actions: ServiceAction[],
  currentSelection: mixed
) => {
  return _.filter(actions, action => {
    return get(action, ['key']) === currentSelection || !get(action, ['hide']);
  });
};

export const superHumanize = (string: string) => {
  // the standard str.humanize kills _id$
  string = _str
    .underscored(string)
    .replace(/_/g, ' ')
    .replace(/\[\]/g, ' ');
  const out = _str.titleize(_str.capitalize(string));
  const words = _str.words(out);
  const subs = {
    Url: 'URL',
    Uri: 'URI',
    Html: 'HTML',
    Id: 'ID',
    id: 'ID',
  };
  _.each(subs, function(v, k) {
    const index = words.indexOf(k);
    if (index !== -1) {
      words[index] = v;
    }
  });
  return words.join(' ');
};

export const normalizeActions = (actions: ServiceAction[]): ServiceAction[] => {
  actions = actions.slice(0);
  if (!_.some(actions, action => get(action, ['important']))) {
    return actions.map(action => {
      const label = exists(action.label)
        ? action.label
        : superHumanize(action.key);
      return update(
        [['label', $set(label)], ['important', $set(true)]],
        action
      );
    });
  }
  return actions;
};

export const parseActions = (
  actions: ServiceAction[],
  currentSelection: mixed
) => {
  /* special parsing rules to help the frontend.
   * 1. if all fields are `important`: false or not defined, set them all to true
   * 2. give every action a label if it doesnt have one
   * 3. strip hidden actions (unless it is currently selected one)
   */
  actions = normalizeActions(actions);
  return visibleActions(actions, currentSelection);
};

export const getActionMeta = (actions: ServiceAction[], actionKey: string) => {
  actions = parseActions(actions, actionKey);
  return _.find(actions, action => action.key === actionKey);
};

export const getActionName = (
  action: ?ServiceAction,
  { hasNoVerb = false }: { hasNoVerb: boolean }
) => {
  if (action == null) {
    return '';
  }

  if (hasNoVerb && action.noun) {
    // hasNoVerb == wants a noun back
    return action.noun;
  }

  let name;
  if (action.label != null) {
    name = action.label;
  } else {
    name = superHumanize(action.key);
  }

  if (hasNoVerb) {
    // clean name and split into words
    let words = _str.words(
      name
        .replace(/\([^)]+\)/g, '') // (some text)
        .replace(/\[[^\]]+\]/g, '') // [some text]
        .replace(/\s{2,}/g, ' ') // dupe white spaces
    );

    if (words.length > 1) {
      const flags = ['new', 'old', 'deprecated', 'legacy'];
      const verbs = [
        'new',
        'updated',
        'update',
        'changed',
        'delete',
        'create',
        'added',
        'add',
        'set',
        'send',
        'submit',
        'call',
        'order',
        'catch',
        'copy',
        'append',
        'moved',
        'move',
        'remove',
        'change',
        'turn',
        'start',
        'stop',
        'post',
        'record',
        'grant',
        'subscribe',
        'make',
      ];
      const articles = ['a', 'an', 'the'];
      const prepositions = ['on', 'for', 'to', 'in', 'from', 'within', 'by'];
      const conjuctions = ['and', 'or', 'but', 'nor'];
      let verbFound = -1;
      let nounFound = -1;
      let approvedWords = [];
      words.every((word, index, array) => {
        const lowerWord = word.toLowerCase();
        // No word
        if (/^[-]$/.test(word)) {
          return true;
        }
        // Articles can always be missed
        if (articles.indexOf(lowerWord) !== -1) {
          return true;
        }
        // Flags
        // 'Deprecated - Create Contact' > 'Contact',
        // 'Create Contact - Deprecated' > 'Contact',
        if (
          (index === 0 || index === array.length) &&
          flags.indexOf(lowerWord) !== -1
        ) {
          return true;
        }
        // Remove list when it has a verb
        // 'Create/Whatever Tracker' > 'Tracker'
        // 'Create Post/Copy' > 'Post/Copy'
        if (
          nounFound === -1 &&
          verbFound === -1 &&
          lowerWord.split(/\\|\//).some(val => verbs.indexOf(val) !== -1)
        ) {
          verbFound = index;
          return true;
        }
        // Remove list when it has a conjunction before finding a noun
        // 'New or Updated Spreadsheet Row' > 'Updated Spreadsheet Row'
        if (nounFound === -1 && conjuctions.indexOf(lowerWord) !== -1) {
          return true;
        }
        if (prepositions.indexOf(lowerWord) !== -1) {
          // Nothing following preposition will add meaning to noun
          // 'Create a Tracker on Google' > 'Tracker'
          if (nounFound !== -1) {
            return false;
          } else {
            // Cannot expect noun after preposition
            // 'Post to Facebook' > 'Post to Facebook'
            // 'Subscribe to Campaign' > 'Subscribe to Campaign'
            approvedWords = words;
            return false;
          }
        }
        if (nounFound === -1) {
          nounFound = index;
        }
        approvedWords.push(word);
        return true;
      });
      if (approvedWords.length > 0) {
        words = approvedWords;
      }
    }
    name = words.join(' ');
  }

  name = _str.trim(name);
  return name;
};

export const actionHelpTextHTML = (action: ServiceAction) => {
  // This flag is put here by the reducer. So depending on it is a bit of a
  // smell. But it's probably non-trivial to refactor that out.
  if (action.hasCreate) {
    return `<p>${action.help_text} Optionally, create one if none are found.</p>`;
  }

  return action.help_text_html;
};

export const actionHelpText = (action: ServiceAction) => {
  return decodeHTMLEntities(actionHelpTextHTML(action).replace(/<[^>]*>/g, ''));
};

export const prettyType = (typeOf: string) => {
  return PRETTY_TYPE_BY_TYPE[typeOf];
};

export const fromCache = (
  cache: {},
  keys: string[],
  itemForCacheFn: Function,
  convertFromCacheFn: Function = item => item
) => {
  // Try to get the item from the cache.
  const cachedItem = get(cache, keys);
  let convertedCacheItem = cachedItem;
  if (exists(cachedItem)) {
    convertedCacheItem = convertFromCacheFn(cachedItem);
  }
  // If it's not in the cache, add it using the function provided.
  if (!exists(convertedCacheItem)) {
    const itemToCache = itemForCacheFn();
    // If we couldn't even get an item to add to the cache, might as well bail.
    if (!exists(itemToCache)) {
      return undefined;
    }
    set(cache, keys, itemToCache);
    convertedCacheItem = convertFromCacheFn(itemToCache);
  }
  return convertedCacheItem;
};

export const prefixWithArticle = (word: string) => {
  if (vowels[word[0].toLowerCase()]) {
    return 'an ' + word;
  } else {
    return 'a ' + word;
  }
};

export const possessive = (string: string) => {
  return string + (string.slice(-1) === 's' ? "'" : "'s");
};

export const isListChild = (field: {}) => {
  return get(field, ['parent', 'parent', 'list'], false);
};

export const firstItemIfArray = (thing: mixed) => {
  if (Array.isArray(thing)) {
    return _.head(thing);
  }
  return thing;
};

export const cleanObjectOfFormaticKeys = (obj: {}) => {
  return _.chain(obj)
    .map((v, k) => (String(k).indexOf('$$__temp__') === 0 ? null : [k, v]))
    .compact()
    .fromPairs()
    .value();
};

export const cleanObjectOfEmptyValues = <O: {}>(obj: O): O => {
  const emptyAndNotNum = (v: any, k: string) => {
    // prune formatic "empty" objects of {$$__temp__: ''}
    if (v && _.isObject(v) && !_.isArray(v) && !_.isEmpty(v)) {
      v = cleanObjectOfFormaticKeys(v);
    }
    // a normal empty object like {} or ''
    if (!_.isNumber(v) && !_.isBoolean(v) && _.isEmpty(v)) {
      return null;
    }
    // an "empty" array like ['']
    if (_.isArray(v) && v.length === 1 && v[0] === '') {
      return null;
    }
    // an "empty" object like {'': ''}
    if (
      _.isObject(v) &&
      _.keys(v).length === 1 &&
      _.keys(v)[0] === '' &&
      v[''] === ''
    ) {
      return null;
    }
    // otherwise return the object
    return [k, v];
  };
  const cleanPairs = _.chain(obj)
    .map(emptyAndNotNum)
    .compact()
    .value();
  const wasAlreadyClean =
    _.isObject(obj) && cleanPairs.length === Object.keys(obj).length;

  // $FlowFixMe I'm not going to fight with flow and lodash
  return wasAlreadyClean ? obj : _.fromPairs(cleanPairs);
};

export const firstIndexOfArrayDifference = (a: mixed, b: mixed) => {
  if (!Array.isArray(a) || !Array.isArray(b)) {
    return -1;
  }
  let i = 0;
  for (i = 0; i < a.length; i++) {
    if (a[i] !== b[i]) {
      return i;
    }
  }
  if (b.length > a.length) {
    return a.length;
  }
  return -1;
};

export const safeExtend = (...args: Array<{}>) => {
  const saveKeys = [];
  const expectedKeyCount = args.reduce((prev, curr) => {
    if (_.isObject(curr)) {
      const keys = Object.keys(curr);
      saveKeys.push(keys);
      return keys.length + prev;
    }
    return prev;
  }, 0);
  const result = _.extend(...args);
  const resultKeyCount = Object.keys(result).length;
  if (resultKeyCount !== expectedKeyCount) {
    const counts = _.countBy(_.flatten(saveKeys), key => key);
    const collisionKeys = Object.keys(counts).filter(key => counts[key] > 1);
    throw new Error(
      'safeExtend expected no key collisions, but these keys collided: ' +
        collisionKeys.join(', ')
    );
  }
  return result;
};

export const interpose = (
  inputList: mixed[],
  funcOrMiddle: mixed
): Array<any> => {
  /* i bet could this be done with _.reduce, blargh
   *  > CommonUtils.interpose([1, 2, 4, 5], 0) # or...
   *  > CommonUtils.interpose([1, 2, 4, 5], ((item, nextItem) -> 0))
   *  > [1, 0, 2, 0, 4, 0, 5]
   */
  const nestedOutList = _.map(inputList, function(item, i) {
    let middleItem;
    const nextItem = inputList[i + 1];
    if (nextItem) {
      if (typeof funcOrMiddle === 'function') {
        middleItem = funcOrMiddle(item, nextItem, i);
      } else {
        middleItem = funcOrMiddle;
      }
      return [item, middleItem];
    }
    return [item];
  });
  return _.flatten(nestedOutList);
};

export const compactObject = (obj: {}) => {
  // prune keys with falsy values
  _.each(obj, function(v, k) {
    if (!v) {
      delete obj[k];
    }
  });
  return obj;
};

export const wordenshtein = (str1: string, str2: string) => {
  /* returns a 0-100 score on how fair apart two strings are
   * ignore punctuation, sort words, recombine and
   * then perform regular levenshtein distance
   */
  str1 = str1.toLowerCase().replace(/[^\w ]/g, ' ');
  str1 = _str
    .words(str1)
    .sort()
    .join(' ');

  // again...
  str2 = str2.toLowerCase().replace(/[^\w ]/g, ' ');
  str2 = _str
    .words(str2)
    .sort()
    .join(' ');

  // moving on...
  return _str.levenshtein(str1, str2);
};

export const flattenObj = (input: {}, splitter: ?string) => {
  let flatObject, k, x;
  if (splitter == null) {
    splitter = '__';
  }
  const output = {};
  for (k in input) {
    if (!input.hasOwnProperty(k)) {
      continue;
    }
    if (typeof input[k] === 'object') {
      flatObject = flattenObj(input[k], splitter);
      for (x in flatObject) {
        if (!flatObject.hasOwnProperty(x)) {
          continue;
        }
        output[k + splitter + x] = flatObject[x];
      }
    } else {
      output[k] = input[k];
    }
  }
  return output;
};

export const unflatten = (input: {}, splitter: ?string) => {
  let k, key, part, parts, t;
  if (splitter == null) {
    splitter = '__';
  }
  const output = {};
  for (k in input) {
    t = output;
    parts = k.split(splitter);
    key = parts.pop();
    while (parts.length) {
      part = parts.shift();
      t = t[part] = t[part] || {};
    }
    t[key] = input[k];
  }
  return output;
};

export const poll = (
  pollFn: Function,
  testFn: Function,
  intervalMs: number,
  timeoutMs: number
) => {
  return (new Promise((resolve, reject) => {
    let totalMs = 0;
    const pollOnce = () => {
      setTimeout(() => {
        pollFn()
          .then(result => {
            if (testFn(result)) {
              resolve(result);
            } else {
              totalMs += intervalMs;
              if (totalMs < timeoutMs) {
                pollOnce();
              } else {
                reject(new Error('timeout'));
              }
            }
          })
          // maybe want to ignore errors and just keep trying
          .catch(reject);
      }, intervalMs);
    };
    pollOnce();
  }): Promise<void>);
};

// Make a clone like underscores clone, that clones inherited properties.
// DON'T USE THIS EXCEPT TO PATCH LEGACY BUGS.
export const cloneAll = (obj: {}) => {
  const clone = {};
  _.forIn(obj, (value, key) => {
    clone[key] = value;
  });
  return clone;
};

export const escapeRegExp = function(str: ?string): string {
  if (str == null) {
    return '';
  }
  return String(str).replace(/([.*+?^=!:${}()|[\]/\\])/g, '\\$1');
};

const defaultToWhiteSpace = function(characters: any) {
  if (characters == null) {
    return '\\s';
  } else if (typeof characters === 'object' && characters.source) {
    return characters.source;
  } else {
    return '[' + escapeRegExp(characters) + ']';
  }
};

// Slugify like we used to have, ugh.
// This is dumb, but I guess underscore.string changed the way its slugify works, and we have legacy code that depends
// on the old generated strings. So providing this "classic" version for that purpose. This should die at some point.
export const classicSlugify = function(str: ?string) {
  if (str == null) {
    return '';
  }

  const from =
    'Ä…Ã Ã¡Ã¤Ã¢Ã£Ã¥Ã¦Ä‡Ä™Ã¨Ã©Ã«ÃªÃ¬Ã­Ã¯Ã®Å‚Å„Ã²Ã³Ã¶Ã´ÃµÃ¸Ã¹ÃºÃ¼Ã»Ã±Ã§Å¼Åº';
  const to = 'aaaaaaaaceeeeeiiiilnoooooouuuunczz';
  const regex = new RegExp(defaultToWhiteSpace(from), 'g');

  str = String(str)
    .toLowerCase()
    .replace(regex, function(c) {
      const index = from.indexOf(c);
      return to.charAt(index) || '-';
    });

  return _str.dasherize(str.replace(/[^\w\s-]/g, ''));
};

export const isPromise = (thing: any) => {
  return _.isObject(thing) && _.isFunction(thing.then);
};

export const isProductionEnv = () => process.env.NODE_ENV === 'production';

export const deepFreeze = (obj: {}) => {
  Object.freeze(obj);

  Object.getOwnPropertyNames(obj).forEach(function(prop) {
    if (
      Object.prototype.hasOwnProperty.call(obj, prop) &&
      obj[prop] !== null &&
      (typeof obj[prop] === 'object' || typeof obj[prop] === 'function') &&
      !Object.isFrozen(obj[prop])
    ) {
      deepFreeze(obj[prop]);
    }
  });

  return obj;
};

export const subscribeAnimationFrame = (onFrame: Function) => {
  let isListening = false;
  const listenToFrame = () => {
    requestAnimationFrame((...args) => {
      if (isListening) {
        onFrame(...args);
        listenToFrame();
      }
    });
  };
  isListening = true;
  listenToFrame();
  return () => {
    isListening = false;
  };
};

/* prefer 128 for 1) retina support and 2) that is the base size from /generated/global-logos.css */
export const getImageFallback = (
  images: ?StringMap,
  size: number = 128
): string => {
  if (images && images[`url_${size}x${size}`]) {
    return images[`url_${size}x${size}`];
  }
  return `${z.cfg.mediaURL}images/frontend/placeholder/${size}x${size}.grey-light.png`;
};

// Will return params with curlies replaced by whatever modify callback returns.
// `modify` is passed `id` and `key` as arguemnts.
export const replaceParams = (
  params: any,
  modify: Function,
  options: Object = {}
) => {
  if (_.isString(params)) {
    return params.replace(/{{(([0-9]+)__)?([^}]+)}}/g, function(
      curly,
      prefix,
      id,
      key
    ) {
      return modify(id, key, curly);
    });
  } else if (Array.isArray(params)) {
    return params.map(value => replaceParams(value, modify, options));
  } else if (_.isObject(params)) {
    const newParams = _.fromPairs(
      _.map(params, (value, key) => [
        key,
        replaceParams(value, modify, options),
      ])
    );
    // Filters are special snowflakes. The key property of filter clauses is not
    // a curly, but rather just a key. So we have to handle filter clauses as a
    // special case.
    if (options.isFilter) {
      if (Array.isArray(newParams.filter_criteria)) {
        const filterCriteria = newParams.filter_criteria.map(clause => {
          if (_.isObject(clause) && _.isString(clause.key)) {
            // Only do this if there are no curlies here already.
            if (clause.key.indexOf('{') < 0) {
              const curlyClauseKey = `{{${clause.key}}}`;
              const renderedClauseKey = replaceParams(curlyClauseKey, modify);
              return {
                ...clause,
                key: renderedClauseKey.replace('{{', '').replace('}}', ''),
              };
            }
          }
          return clause;
        });
        return {
          ...newParams,
          filter_criteria: filterCriteria,
        };
      }
    }
    return newParams;
  } else {
    return params;
  }
};

const dashMatcherRegex = /-([a-z])/g;
const dashReplacerFn = function(match, group) {
  return group.toUpperCase();
};

export const camelCssKeys = (obj: any) => {
  if (!_.isObject(obj)) {
    return obj;
  }
  return _.fromPairs(
    _.toPairs(obj).map(([dashClassName, value]) => [
      dashClassName.replace(dashMatcherRegex, dashReplacerFn),
      value,
    ])
  );
};

export const plainTextErrorMessage = (error: any) => {
  if (_.isString(error)) {
    return error;
  }
  if (
    _.isBoolean(error) ||
    _.isNumber(error) ||
    _.isNull(error) ||
    _.isUndefined(error)
  ) {
    return String(error);
  }
  if (error.name || error.message) {
    return `${error.name}: ${error.message}`;
  }
  return 'Unknown Error';
};

export const clamp = (min: number, max: number, val: number) =>
  Math.max(min, Math.min(val, max));

// Borrowed from MDN
export const truncate = Math.trunc || ((num: number) => num - (num % 1));

export const ensureArray = <T>(value: T | Array<T>): Array<T> =>
  Array.isArray(value) ? value : [value];

export const flattenReduce = (flatArr: [], nestedArr: []) => {
  return [...flatArr, ...ensureArray(nestedArr)];
};

export const uniqueFilter = (item: any, idx: number, arr: []) => {
  return arr.indexOf(item) === idx;
};

export const arrayToObjectByKey = (key: string, array: ?Array<*>) =>
  (array || []).reduce(
    (obj, item) => ({
      ...obj,
      [item[key]]: item,
    }),
    {}
  );

export const toString = (thing: mixed) => (exists(thing) ? String(thing) : '');

export const toLowerCaseString = (thing: mixed): string =>
  toString(thing).toLowerCase();

export const matchesQuery = (query: string, string: mixed) => {
  if (!exists(string)) {
    return false;
  }
  return toLowerCaseString(string).indexOf(toLowerCaseString(query)) > -1;
};

export const matchesQueryForKeypath = (
  query: string,
  keypath: string | Array<string>,
  obj: {}
) => {
  return matchesQuery(query, _.get(obj, keypath));
};

export const compareStrings = (string1?: string, string2?: string) => {
  const s1 = (string1 || '').toLowerCase();
  const s2 = (string2 || '').toLowerCase();

  if (s1 < s2) {
    return -1;
  }
  return s1 === s2 ? 0 : 1;
};

type SortOrderOrTitleArg = {
  sortOrder?: number,
  title?: string,
};

export const compareBySortOrderOrTitle = (
  f1: SortOrderOrTitleArg,
  f2: SortOrderOrTitleArg
) => {
  if (f1.sortOrder && f2.sortOrder) {
    return f1.sortOrder - f2.sortOrder;
  }

  if (f1.sortOrder || f2.sortOrder) {
    return f1.sortOrder ? -1 : 1;
  }

  return compareStrings(f1.title, f2.title);
};

export const compareVersions = (version1: string, version2: string) => {
  if (version1 === version2) {
    return 0;
  }
  const v1s = version1.split('.').map(part => parseInt(part, 10));
  const v2s = version2.split('.').map(part => parseInt(part, 10));
  for (let i = 0; i < Math.max(v1s.length, v2s.length); i++) {
    const v1i = v1s[i] || 0;
    const v2i = v2s[i] || 0;
    if (v1i !== v2i) {
      return v1i - v2i;
    }
  }
  return v1s.length - v2s.length;
};

export const isEqualIgnoreCase = (string1: string, string2: string) =>
  compareStrings(string1, string2) === 0;

export const infinitize = (num: number | string) =>
  num === -1 || num === 'unlimited' ? Infinity : Number(num);

export const uninfinitizeWith = (alt: number, num: number) =>
  num === Infinity ? alt : num;

export const uninfinitize = _.partial(uninfinitizeWith, 'unlimited');

/**
 * Given an object containing a components props, will return an object that
 * contains the most commonly used input props.
 */
export const pickCommonInputProps = (props: {}) =>
  _.pick(props, [
    'name',
    'placeholder',
    'type',
    'value',
    'maxLength',
    'minLength',
  ]);

export const removeSpaces = (s: string = '') => s.replace(/\s/g, '');

export const removeConsecutiveSpaces = _str.clean;

export const removeNonDigits = (string: string) =>
  String(string || '').replace(/\D/g, '');

const createLogicalCompositorWith = (method: string) => (
  ...predicates: any
) => (...args: []) => predicates[method](predicate => predicate(...args));

export const andify = createLogicalCompositorWith('every');
export const orify = createLogicalCompositorWith('some');

export const partialWithDefault = (
  fn: Function,
  outerArgs: mixed[] = [],
  defaultValue: any
) => (...innerArgs: mixed[]) => {
  const result = fn(...ensureArray(outerArgs), ...innerArgs);
  return result === undefined || result === null ? defaultValue : result;
};

export const getCookie = (id: string) => {
  let result = null;

  _.forEach(document.cookie.split('; '), cookie => {
    const [name, value] = cookie.split('=');

    if (name === id) {
      result = value;
      return;
    }
  });

  return result;
};

export const setCookie = (
  id: string,
  value: string,
  maxAge: number,
  path: string = '/'
) => {
  const maxAgeValue = maxAge ? `; max-age=${maxAge}` : '';
  document.cookie = `${id}=${value}${maxAgeValue}; path=${path}`;
};

// thanks emailregex.com
export const isValidEmail = (email: string) => {
  /* eslint-disable no-useless-escape */
  const emailMatch = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  /* eslint-enable no-useless-escape */
  return emailMatch.test(email);
};

export const createId = () => {
  // we used to use a uuid... but... legacy rules indicate the
  // group and rule ids need to be 64-bit signed integers :'(
  // but, of course, javascript's integers are only 53-bits, so
  // we use MAX_SAFE_INTEGER for a base
  return Math.floor(Math.random() * (Math.pow(2, 53) - 1));
};

export const getInitialsFromString = (str: string): string[] => {
  return str.split(/\s+/).map(s => s[0]);
};

// display simple scalar value. Display boolean `false` as false, numeric
// zero as '0', but do _not_ display `undefined` or `null` - return ''.
export const stringifyScalarValue = (value?: mixed) =>
  _.isUndefined(value) || _.isNull(value) ? '' : String(value);

// Borrowed from http://bit.ly/2oKP2ll
export const downloadFile = (filename: string, content: string) => {
  const el = document.createElement('a');
  const file = new File([content], filename, { type: 'application/json' });
  const href = URL.createObjectURL(file);
  el.setAttribute('href', href);
  el.setAttribute('download', filename);
  el.style.display = 'none';
  if (document.body !== null) {
    document.body.appendChild(el);
  }
  el.click();
  if (document.body !== null) {
    document.body.removeChild(el);
  }
};

export const getVisibilityChangeProps = () => {
  if (typeof document.hidden !== 'undefined') {
    // This covers most cases
    return {
      hidden: 'hidden',
      visibilityChange: 'visibilitychange',
    };
  } else if (typeof (document: Object).msHidden !== 'undefined') {
    // Internet Explorer and Microsoft Edge
    return {
      hidden: 'msHidden',
      visibilityChange: 'msvisibilitychange',
    };
  } else if (typeof (document: Object).webkitHidden !== 'undefined') {
    // Older versions of webkit browsers (Chrome, Safari, newer versions of Opera, almost all iOS browsers (including Firefox for iOS))
    return {
      hidden: 'webkitHidden',
      visibilityChange: 'webkitvisibilitychange',
    };
  }
  return undefined;
};

export const basename = (path: string) => {
  return path.slice(path.lastIndexOf('/') + 1);
};

export const matchTokensAndSpace = /({{|}}|\s)/;

export const hasOpenTag = (stringWithTokens: string = '') => {
  const openTag = '{{';
  const closeTag = '}}';
  const parts = stringWithTokens.split(matchTokensAndSpace);

  return parts.some((part, i, allParts) => {
    const isText = part !== openTag && part !== closeTag;
    const isUnclosed =
      allParts[i - 1] === openTag && allParts[i + 1] !== closeTag;

    return isText && isUnclosed;
  });
};

export const camelizeObjKeys = (obj: {}) =>
  _.transform(
    obj,
    (result, val, key) => {
      const camelizeVal = _.isPlainObject(val) ? camelizeObjKeys(val) : val;
      Object.assign(result, { [_.camelCase(key)]: camelizeVal });
    },
    {}
  );

export const uuidv4 = () =>
  '10000000-1000-4000-8000-100000000000'.replace(/[018]/g, c => {
    const i = parseInt(c, 10);
    return (
      i ^
      ((window.crypto || window.msCrypto).getRandomValues(
        new Uint8Array(1)
      )[0] &
        (15 >> (i / 4)))
    ).toString(16);
  });
